/* @flow */

import type { SortableProductList } from "shop-state/types";

import React, { useState, useMemo, useEffect, useContext } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useData, useSendMessage } from "crustate/react";
import { useTranslate } from "@awardit/react-use-translate";
import { AllProductsData, CurrentPageInfoData, CustomerData } from "data";
import { allProducts as loadAllProducts } from "@crossroads/shop-state/all-products";
import { OffCanvasFilterMenu, useFilter, ActiveFilters } from "@crossroads/ui-components";
import { Title } from "components/UiComponents";
import { parseParams } from "helpers/location-search-string";
import { useOpenFiltermenu } from "helpers/use-open-filtermenu";
import { constructSortableValues } from "helpers/filters";
import { tryParseJSONObject } from "helpers/utils";
import { getCurrentPageInfo } from "state/current-page-info";
import { StoreInfoContext } from "entrypoint/shared";
import { PAGE_SIZE } from "effects/route";
import Wrapper from "components/Wrapper";
import ProductList from "components/ProductList";
import ProductCarousel from "components/ProductCarousel";
import Filterbar from "components/Filterbar";
import Breadcrumbs from "components/Breadcrumbs";
import PopularCategoryCarousel from "components/PopularCategoryCarousel";
import CurrentPageInfo from "components/CurrentPageInfo";
import PaginatedProductList from "components/CategoryView/paginated-product-list";

import styles from "./styles.scss";
import cn from "classnames";

type Props = {
  allProductsData: SortableProductList,
  updating: boolean,
  title: string,
};

const AllProductsViewWrapper = (): |
  React$Element<(Props) => React$Element<"div">> |
  React$Element<({ title: string, ... }) => React$Node> => {
  const all = useData(AllProductsData);
  const {
    content: {
      allproductsview: { pageTitle },
    },
  } = useContext(StoreInfoContext);

  if (all.state === "LOADED" || all.state === "UPDATING") {
    return (
      <AllProductsView
        title={pageTitle ?? ""}
        updating={all.state === "UPDATING"}
        allProductsData={all.data}
      />
    );
  }

  return <HintAllProductsView title={pageTitle ?? ""} />;
};

const AllProductsView = ({ allProductsData, updating, title }: Props) => {
  const t = useTranslate();
  const location = useLocation();
  const customerData = useData(CustomerData);
  const sendMessage = useSendMessage();
  const params = useMemo(() => parseParams(location.search), [location.search]);
  const numPages = Math.ceil(allProductsData.totalCount / PAGE_SIZE);
  const openFiltermenu = useOpenFiltermenu();
  const currentPageInfoData = useData(CurrentPageInfoData);
  const customer = customerData.state === "LOGGED_IN" ? customerData.data : null;
  const nonLinearSlider = true;
  const {
    routes: {
      allProductsView,
    },
    content: {
      allproductsview: {
        popularCategories,
        popularCategoriesTitle,
        autoFilterFromPoints,
        autoFilterMinPoints,
      },
    },
  } = useContext(StoreInfoContext);

  const popularCategoriesJson = popularCategories !== undefined &&
    tryParseJSONObject(popularCategories) ? JSON.parse(popularCategories) : "";

  const gotCurrentPageInfo = (currentPageInfoData.data?.allproductsview?.length || 0) > 0;
  const { content: { productCarousel: { useOnAllProductsView } } } = useContext(StoreInfoContext);

  useEffect(() => {
    sendMessage(getCurrentPageInfo("ALLPRODUCTSVIEW"));
  }, []);

  const [page, setPage] = useState(() =>
    Math.max(1, parseInt(params.page, 10) || 1)
  );

  const useFilterStateConfig = {
    loading: updating,
    productList: allProductsData,
    usePoints: true,
    incVat: false,
    load: location => {
      sendMessage(loadAllProducts(location, false));
    },
  };

  const filterState = useFilter(useFilterStateConfig);

  // Update page when URL is updated
  useEffect(() => {
    setPage(parseInt(params.page, 10) || 1);
  }, [params]);

  return (
    <div>
      <Helmet title={title} />

      {gotCurrentPageInfo &&
        <CurrentPageInfo className={styles.currentPageInfo} />
      }

      <Wrapper variant="pageWrapper" className={cn("awardit-allProductsViewWrapper", styles.wrapper)}>
        <Breadcrumbs className="awardit-allProductsViewBreadCrumbs" current={title} />

        {(popularCategoriesJson && page === 1) &&
          <div className={styles.popularCategoryCarousel}>
            <PopularCategoryCarousel
              popularCategories={popularCategoriesJson}
              title={popularCategoriesTitle ?? ""}
            />
          </div>
        }

        {page === 1 && Boolean(useOnAllProductsView) &&
          <div className={styles.popularProducts}>
            <ProductCarousel />
          </div>
        }

        <div className={styles.header}>
          <Title className="h2">{title}</Title>
          <p className={styles.total_count}>{t("FILTER.ACTIVE_FILTERS.TOTAL_COUNT", { count: filterState.totalCount })}</p>
        </div>

        <div className={styles.activeFiltersRow}>
          {filterState.active.filters.length > 0 &&
            <h3 className={cn("h4", styles.activeFiltersRowHeader)}>
              {t("FILTER.ACTIVE_FILTERS.COUNT")}
              <span className={styles.activeFiltersRowCount}>{` (${filterState.active.filters.length})`}</span>
            </h3>
          }
          <ActiveFilters
            hideEmptyFilters
            filterState={filterState}
            className={styles.activeFilters}
          />
        </div>

        <Filterbar
          openFilters={openFiltermenu.openFiltermenu}
          className={styles.filterbar}
          content={{
            autoFilterFromPoints: Boolean(autoFilterFromPoints),
            autoFilterMinPoints: autoFilterMinPoints ?? "",
          }}
          route={allProductsView}
          user={customer}
          filterState={{
            ...filterState,
            sort: {
              ...filterState.sort,
              values: Array.isArray(filterState.sort.values) ?
                constructSortableValues(filterState.sort.values, t) :
                [],
            },
          }} />

        <PaginatedProductList
          updating={updating}
          page={page}
          numPages={numPages}
          perPage={PAGE_SIZE}
          productList={allProductsData}
          className="awardit-allProductsViewProductList"
          category={title}
        />

        <OffCanvasFilterMenu
          isOpen={openFiltermenu.isOpen}
          close={openFiltermenu.closeFiltermenu}
          filterState={filterState}
          nonLinearSlider={nonLinearSlider}
          exponentiation={4}
          containerClass="awardit-OffCanvasFilterMenu"
        />
      </Wrapper>
    </div>
  );
};

export const HintAllProductsView = ({ title }: { title: string }): React$Node => {
  return (
    <div className={styles.block}>
      <Wrapper variant="pageWrapper" className={cn("awardit-allProductsViewWrapper", styles.wrapper)}>
        <Helmet title={title} />

        <Breadcrumbs current={title} />

        <Title>{title}</Title>
        <p className={styles.total_count} />

        <ProductList products={[null, null, null, null, null, null, null, null]} />
      </Wrapper>
    </div>
  );
};

export default AllProductsViewWrapper;
